import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Search, ArrowUp, ArrowDown, Download } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatINR, formatNum } from "@/lib/api";
import { logError } from "@/lib/logger";

const PAGE_SIZE = 20000; // single-page mode — fetch everything, no paginator

// Money-typed columns per KPI (used for sum-totals row + CSV header)
const MONEY_COLS = {
  bookings:     ["total_revenue", "recognised_revenue", "collected"],
  revenue:      ["total_tov", "monthly_portion", "recognised_amount", "collected", "outstanding", "advance"],
  collection:   ["amount_paid"],
  "collection-orphan": ["amount_paid"],
  outstanding:  ["recognised", "collected", "outstanding"],
  dropouts:     ["total_tov", "recognised_amount", "collected", "refund_eligible", "lost_bookings"],
  students:     ["total_revenue", "recognised_revenue", "collected", "outstanding", "advance"],
  partnerships: ["total_revenue", "recognised_revenue", "collected", "outstanding"],
  salespeople:  ["revenue", "recognised_revenue", "collection", "outstanding", "avg_deal_size"],
};

// Human-friendly column labels — used by CSV header + Totals row.
// Sticks to the same order the body table renders.
const COLUMN_LABELS = {
  // common
  enrolment_date: "Enrolled",
  payer_name: "Payer",
  name: "Payer",
  payer_type: "Payer Type",
  client_type: "Client Type",
  status: "Status",
  program: "Program",
  cohort: "Cohort",
  sales_person: "Sales Person",
  vertical: "Vertical",
  // money / metric
  total_revenue: "Booking (TOV)",
  total_tov: "Booking (TOV)",
  recognised_revenue: "Revenue",
  recognised_amount: "Revenue",
  monthly_portion: "Monthly",
  months_in_range: "Mo. in range",
  collected: "Collected",
  amount_paid: "Amount",
  outstanding: "Outstanding",
  advance: "Advance",
  refund_eligible: "Refund Eligible",
  lost_bookings: "Lost Bookings",
  dropout_date: "Dropped on",
  adjustment: "Adjustment",
  aging_days: "Aging (days)",
  aging_bucket: "Aging bucket",
  // payments
  payment_date: "Date",
  payment_status: "Status",
  // salespeople
  designation: "Designation",
  students: "# Students",
  partnerships: "# Partnerships",
  revenue: "Booking (TOV)",
  collection: "Collected",
  avg_deal_size: "Avg deal",
};

// Order of fields per kpi for CSV export (mirrors the visible table)
const CSV_COLUMNS = {
  bookings:     ["enrolment_date", "name", "payer_type", "program", "cohort", "sales_person", "total_revenue", "recognised_revenue", "collected"],
  revenue:      ["enrolment_date", "payer_name", "payer_type", "program", "cohort", "sales_person", "total_tov", "monthly_portion", "months_in_range", "recognised_amount", "collected", "outstanding", "advance"],
  collection:   ["payment_date", "payer_name", "payer_type", "program", "cohort", "sales_person", "payment_status", "amount_paid"],
  "collection-orphan": ["payment_date", "payer_name", "payer_type", "program", "cohort", "sales_person", "payment_status", "amount_paid"],
  outstanding:  ["enrolment_date", "payer_name", "payer_type", "program", "cohort", "sales_person", "recognised", "collected", "outstanding", "aging_days", "aging_bucket"],
  dropouts:     ["payer_name", "payer_type", "enrolment_date", "dropout_date", "program", "cohort", "total_tov", "recognised_amount", "collected", "refund_eligible", "lost_bookings"],
  students:     ["enrolment_date", "name", "payer_type", "status", "program", "cohort", "sales_person", "total_revenue", "recognised_revenue", "collected", "outstanding", "advance"],
  partnerships: ["enrolment_date", "name", "payer_type", "status", "program", "cohort", "sales_person", "total_revenue", "recognised_revenue", "collected", "outstanding"],
  salespeople:  ["name", "designation", "students", "partnerships", "avg_deal_size", "revenue", "recognised_revenue", "outstanding"],
};

const _csvEscape = (v) => {
  if (v == null) return "";
  const s = Array.isArray(v) ? v.join("; ") : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCSV = (kpi, rows, titleLabel) => {
  if (!rows || rows.length === 0) return;
  const cols = CSV_COLUMNS[kpi] || Object.keys(rows[0]);
  const header = cols.map((c) => _csvEscape(COLUMN_LABELS[c] || c)).join(",");
  const lines = rows.map((r) =>
    cols.map((c) => _csvEscape(r[c])).join(",")
  );
  const blob = new Blob([header + "\n" + lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeTitle = (titleLabel || kpi).replace(/[^a-zA-Z0-9_-]+/g, "_");
  a.href = url;
  a.download = `${safeTitle}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const TotalsRow = ({ kpi, rows }) => {
  const money = MONEY_COLS[kpi] || [];
  if (!money.length || !rows || rows.length === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs"
      data-testid="drill-totals"
    >
      <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mr-1">
        Totals
      </span>
      {money.map((col) => {
        const sum = rows.reduce((acc, r) => acc + (Number(r[col]) || 0), 0);
        return (
          <span
            key={col}
            className="inline-flex items-center gap-1.5 tabular"
            data-testid={`drill-total-${col}`}
          >
            <span className="text-muted-foreground">{COLUMN_LABELS[col] || col}</span>
            <span className="font-semibold text-foreground">{formatINR(sum)}</span>
          </span>
        );
      })}
      <span className="ml-auto text-muted-foreground tabular">
        {formatNum(rows.length)} record{rows.length === 1 ? "" : "s"}
      </span>
    </div>
  );
};

const PAYER_TYPE_COLOR = {
  Student: "#1F3A5F",
  Partnership: "#E76F1A",
};

const STATUS_COLOR = {
  Active: "#2BA88A",
  Inactive: "#94a3b8",
};

const AGING_COLOR = {
  "Current": "#94a3b8",
  "0-30d": "#2BA88A",
  "31-60d": "#D6A02E",
  "61-90d": "#E76F1A",
  "90d+": "#B23A48",
};

const Tag = ({ label, color, testId }) => (
  <span
    className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
    style={{ backgroundColor: `${color}1A`, color }}
    data-testid={testId}
  >
    {label}
  </span>
);

const SortHeader = ({ id, label, sort, onSort, align = "left" }) => {
  const active = sort?.by === id;
  const dir = active ? sort.dir : null;
  return (
    <button
      type="button"
      onClick={() =>
        onSort({ by: id, dir: active && dir === "desc" ? "asc" : "desc" })
      }
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold ${
        active ? "text-[#1F3A5F]" : "text-muted-foreground"
      } ${align === "right" ? "justify-end w-full" : ""}`}
      data-testid={`drill-sort-${id}`}
    >
      {label}
      {active &&
        (dir === "desc" ? (
          <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUp className="w-3 h-3" />
        ))}
    </button>
  );
};

// Pagination removed in iter15 — drill-downs now render every record in one
// scrollable view (page_size = 20000). See `summary` span for the count badge.

// Small helpers shared across bodies
const ListCell = ({ items, className = "" }) => (
  <td className={`py-2 pr-3 text-xs text-muted-foreground ${className}`}>
    {(items || []).join(", ") || "—"}
  </td>
);

const MoneyCell = ({ value, color, bold = false, className = "" }) => (
  <td
    className={`py-2 pr-3 text-right tabular whitespace-nowrap ${bold ? "font-semibold" : ""} ${className}`}
    style={color ? { color } : undefined}
  >
    {formatINR(value)}
  </td>
);

const Th = ({ children, align = "left", className = "" }) => (
  <th
    className={`py-2 pr-3 ${align === "right" ? "text-right" : "text-left"} ${className}`}
  >
    {children}
  </th>
);

// -------------------- Drill-down body renderers --------------------

// Bookings: Enrolled, Payer, Payer Type, Program, Cohort, Sales Person, Booking (TOV), Revenue, Collected
const BookingsBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="enrolment_date" label="Enrolled" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_name" label="Payer" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_type" label="Payer Type" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="program" label="Program" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="cohort" label="Cohort" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="sales_person" label="Sales Person" sort={sort} onSort={onSort} /></Th>
        <Th align="right"><SortHeader id="total_revenue" label="Booking (TOV)" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="recognised_revenue" label="Revenue" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right" className="pr-0"><SortHeader id="collected" label="Collected" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
          <td className="py-2 pr-3 text-xs text-muted-foreground tabular whitespace-nowrap">
            {r.enrolment_date || "—"}
          </td>
          <td className="py-2 pr-3 font-semibold" title={r.name}>{r.name || "—"}</td>
          <td className="py-2 pr-3">
            <Tag label={r.payer_type} color={PAYER_TYPE_COLOR[r.payer_type] || "#94a3b8"} />
          </td>
          <ListCell items={r.program} />
          <ListCell items={r.cohort} />
          <ListCell items={r.sales_person} />
          <MoneyCell value={r.total_revenue} bold />
          <MoneyCell value={r.recognised_revenue} color="#E76F1A" />
          <MoneyCell value={r.collected} color="#2BA88A" className="pr-0" />
        </tr>
      ))}
    </tbody>
  </table>
);

// Revenue: Enrolled, Payer, Payer Type, Program, Cohort, Sales Person, Booking (TOV), Monthly, Mo. in range, Revenue, Collected, Outstanding, Advance
const RevenueBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="enrolment_date" label="Enrolled" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_name" label="Payer" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_type" label="Payer Type" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="program" label="Program" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="cohort_start" label="Cohort" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="sales_person" label="Sales Person" sort={sort} onSort={onSort} /></Th>
        <Th align="right"><SortHeader id="total_tov" label="Booking (TOV)" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="monthly_portion" label="Monthly" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="months_in_range" label="Mo. in range" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="recognised_amount" label="Revenue" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="collected" label="Collected" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="outstanding" label="Outstanding" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right" className="pr-0"><SortHeader id="advance" label="Advance" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
          <td className="py-2 pr-3 text-xs text-muted-foreground tabular whitespace-nowrap">
            {r.enrolment_date || "—"}
          </td>
          <td className="py-2 pr-3 font-semibold" title={r.payer_name}>{r.payer_name}</td>
          <td className="py-2 pr-3">
            <Tag label={r.payer_type} color={PAYER_TYPE_COLOR[r.payer_type] || "#94a3b8"} />
          </td>
          <ListCell items={r.program} />
          <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap">
            {r.is_fallback ? (
              <span title="No cohort window — entire TOV booked on enrolment date">
                {r.cohort_start}
                <div className="text-[10px] opacity-70">(on enrolment)</div>
              </span>
            ) : (
              <>
                <div>{r.cohort_start}</div>
                <div>→ {r.cohort_end}</div>
              </>
            )}
          </td>
          <ListCell items={r.sales_person} />
          <MoneyCell value={r.total_tov} />
          <td className="py-2 pr-3 text-right tabular text-xs whitespace-nowrap">{formatINR(r.monthly_portion)}</td>
          <td className="py-2 pr-3 text-right tabular text-xs whitespace-nowrap">
            {r.months_in_range}
            {r.total_months ? <span className="text-muted-foreground"> / {r.total_months}</span> : ""}
          </td>
          <MoneyCell value={r.recognised_amount} color="#E76F1A" bold />
          <MoneyCell value={r.collected} color="#2BA88A" />
          <MoneyCell value={r.outstanding} color={r.outstanding > 0 ? "#B23A48" : "#94a3b8"} />
          <MoneyCell value={r.advance} color={r.advance > 0 ? "#3B7DD8" : "#94a3b8"} className="pr-0" />
        </tr>
      ))}
    </tbody>
  </table>
);

// Collection: Date, Payer, Payer Type, Program, Cohort, Sales Person, Status, Amount
const CollectionBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="payment_date" label="Date" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_name" label="Payer" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_type" label="Payer Type" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="program" label="Program" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="cohort" label="Cohort" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="sales_person" label="Sales Person" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payment_status" label="Status" sort={sort} onSort={onSort} /></Th>
        <Th align="right" className="pr-0"><SortHeader id="amount_paid" label="Amount" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
          <td className="py-2 pr-3 text-xs text-muted-foreground tabular whitespace-nowrap">
            {r.payment_date || "—"}
          </td>
          <td className="py-2 pr-3 font-semibold" title={r.payer_name}>{r.payer_name}</td>
          <td className="py-2 pr-3">
            <Tag label={r.payer_type} color={PAYER_TYPE_COLOR[r.payer_type] || "#94a3b8"} />
          </td>
          <ListCell items={r.program} />
          <ListCell items={r.cohort} />
          <ListCell items={r.sales_person} />
          <td className="py-2 pr-3 text-xs">{r.payment_status || "—"}</td>
          <MoneyCell value={r.amount_paid} color="#2BA88A" bold className="pr-0" />
        </tr>
      ))}
    </tbody>
  </table>
);

// Outstanding: Enrolled, Payer, Payer Type, Program, Cohort, Sales Person, Revenue, Collected, Outstanding, Aging
const OutstandingBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="enrolment_date" label="Enrolled" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_name" label="Payer" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_type" label="Payer Type" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="program" label="Program" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="cohort" label="Cohort" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="sales_person" label="Sales Person" sort={sort} onSort={onSort} /></Th>
        <Th align="right"><SortHeader id="recognised" label="Revenue" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="collected" label="Collected" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="outstanding" label="Outstanding" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right" className="pr-0"><SortHeader id="aging_days" label="Aging" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
          <td className="py-2 pr-3 text-xs text-muted-foreground tabular whitespace-nowrap">
            {r.enrolment_date || "—"}
          </td>
          <td className="py-2 pr-3 font-semibold" title={r.payer_name}>{r.payer_name}</td>
          <td className="py-2 pr-3">
            <Tag label={r.payer_type} color={PAYER_TYPE_COLOR[r.payer_type] || "#94a3b8"} />
          </td>
          <ListCell items={r.program} />
          <ListCell items={r.cohort} />
          <ListCell items={r.sales_person} />
          <MoneyCell value={r.recognised} color="#E76F1A" />
          <MoneyCell value={r.collected} color="#2BA88A" />
          <MoneyCell value={r.outstanding} color="#B23A48" bold />
          <td className="py-2 pr-0 text-right whitespace-nowrap">
            <div className="inline-flex flex-col items-end gap-0.5">
              <Tag
                label={r.aging_bucket || "—"}
                color={AGING_COLOR[r.aging_bucket] || "#94a3b8"}
              />
              {r.aging_days > 0 && (
                <span className="text-[10px] text-muted-foreground tabular">
                  {r.aging_days}d
                </span>
              )}
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Drop-out exposure: Payer, Dropped on, TOV, Earned, Collected, Refund Eligible, Lost Bookings
const DropoutsBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="payer_name" label="Payer" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_type" label="Type" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="enrolment_date" label="Enrolled" sort={sort} onSort={onSort} /></Th>
        <Th>Dropped on</Th>
        <Th><SortHeader id="program" label="Program" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="cohort" label="Cohort" sort={sort} onSort={onSort} /></Th>
        <Th align="right"><SortHeader id="total_tov" label="Booking (TOV)" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="months_in_range" label="Mo. in range" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="recognised_amount" label="Revenue" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="collected" label="Collected" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="refund_eligible" label="Refund Eligible" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right" className="pr-0"><SortHeader id="lost_bookings" label="Lost Bookings" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr
          key={r.id}
          className={`border-b border-border/60 last:border-0 hover:bg-secondary/40 ${
            r.dropout_missing_date ? "bg-[#B23A48]/[0.06]" : ""
          }`}
          data-testid={`dropout-row-${r.id}`}
        >
          <td className="py-2 pr-3 font-semibold" title={r.payer_name}>
            <div className="flex items-center gap-1.5">
              <span>{r.payer_name}</span>
              {r.investigate && (
                <span
                  className="inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-semibold border border-[#D6A02E]/40 bg-[#D6A02E]/15 text-[#D6A02E] uppercase tracking-wide"
                  title="Collected > 0 but Total Bookings is 0 — verify Airtable record"
                  data-testid="investigate-badge"
                >
                  Investigate
                </span>
              )}
            </div>
          </td>
          <td className="py-2 pr-3">
            <Tag label={r.payer_type} color={PAYER_TYPE_COLOR[r.payer_type] || "#94a3b8"} />
          </td>
          <td className="py-2 pr-3 text-xs text-muted-foreground tabular whitespace-nowrap">
            {r.enrolment_date || "—"}
          </td>
          <td className="py-2 pr-3 text-xs tabular whitespace-nowrap">
            {r.dropout_missing_date ? (
              <Tag label="Missing date" color="#D6A02E" />
            ) : (
              <Tag label={r.dropout_date || "—"} color="#B23A48" />
            )}
          </td>
          <ListCell items={r.program} />
          <ListCell items={r.cohort} />
          <MoneyCell value={r.total_tov} />
          <td className="py-2 pr-3 text-xs tabular text-right whitespace-nowrap">
            {r.months_in_range}
            {r.total_months ? <span className="text-muted-foreground"> / {r.total_months}</span> : ""}
          </td>
          <MoneyCell value={r.recognised_amount} color="#E76F1A" />
          <MoneyCell value={r.collected} color="#2BA88A" />
          <MoneyCell value={r.refund_eligible} color={r.refund_eligible > 0 ? "#D6A02E" : "#94a3b8"} bold />
          <MoneyCell value={r.lost_bookings} color={r.lost_bookings > 0 ? "#B23A48" : "#94a3b8"} className="pr-0" />
        </tr>
      ))}
    </tbody>
  </table>
);


// Students: Enrolled, Payer, Payer Type, Status, Program, Cohort, Sales Person, Booking (TOV), Recognised Revenue, Total Paid, Outstanding, Advance
const StudentsBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="enrolment_date" label="Enrolled" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_name" label="Payer" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_type" label="Payer Type" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="status" label="Status" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="program" label="Program" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="cohort" label="Cohort" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="sales_person" label="Sales Person" sort={sort} onSort={onSort} /></Th>
        <Th align="right"><SortHeader id="total_revenue" label="Booking (TOV)" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="recognised_revenue" label="Revenue" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="collected" label="Total Paid" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="outstanding" label="Outstanding" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right" className="pr-0"><SortHeader id="advance" label="Advance" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
          <td className="py-2 pr-3 text-xs text-muted-foreground tabular whitespace-nowrap">
            {r.enrolment_date || "—"}
          </td>
          <td className="py-2 pr-3 font-semibold" title={r.name}>{r.name || "—"}</td>
          <td className="py-2 pr-3">
            <Tag label={r.payer_type} color={PAYER_TYPE_COLOR[r.payer_type] || "#94a3b8"} />
          </td>
          <td className="py-2 pr-3">
            <Tag label={r.status} color={STATUS_COLOR[r.status] || "#94a3b8"} />
          </td>
          <ListCell items={r.program} />
          <ListCell items={r.cohort} />
          <ListCell items={r.sales_person} />
          <MoneyCell value={r.total_revenue} bold />
          <MoneyCell value={r.recognised_revenue} color="#E76F1A" />
          <MoneyCell value={r.collected} color="#2BA88A" />
          <MoneyCell value={r.outstanding} color={r.outstanding > 0 ? "#B23A48" : "#94a3b8"} />
          <MoneyCell value={r.advance} color={r.advance > 0 ? "#3B7DD8" : "#94a3b8"} className="pr-0" />
        </tr>
      ))}
    </tbody>
  </table>
);

// Salespeople: Sales Person, Designation, # Students, # Partnerships, Avg Deal, Booking (TOV), Revenue, Outstanding
const SalespeopleBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="name" label="Sales Person" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="designation" label="Designation" sort={sort} onSort={onSort} /></Th>
        <Th align="right"><SortHeader id="students" label="# Students" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="partnerships" label="# Partnerships" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="avg_deal_size" label="Avg Deal" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="revenue" label="Booking (TOV)" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="recognised_revenue" label="Revenue" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right" className="pr-0"><SortHeader id="outstanding" label="Outstanding" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
          <td className="py-2 pr-3 font-semibold">{r.name}</td>
          <td className="py-2 pr-3 text-muted-foreground text-xs">{r.designation || "—"}</td>
          <td className="py-2 pr-3 text-right tabular">{formatNum(r.students)}</td>
          <td className="py-2 pr-3 text-right tabular">{formatNum(r.partnerships)}</td>
          <td className="py-2 pr-3 text-right tabular text-muted-foreground whitespace-nowrap">
            {formatINR(r.avg_deal_size)}
          </td>
          <MoneyCell value={r.revenue} bold />
          <MoneyCell value={r.recognised_revenue} color="#E76F1A" />
          <MoneyCell value={r.outstanding} color={r.outstanding > 0 ? "#B23A48" : "#94a3b8"} bold className="pr-0" />
        </tr>
      ))}
    </tbody>
  </table>
);

// Partnerships: Enrolled, Partner Name, Partner Type, Status, Program, Cohort (start→end), Sales Person, Booking (TOV), Revenue, Total Paid, Outstanding
const PartnershipsBody = ({ data, sort, onSort }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left border-b border-border sticky top-0 bg-background z-10">
        <Th><SortHeader id="enrolment_date" label="Enrolled" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_name" label="Partner Name" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="payer_type" label="Partner Type" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="status" label="Status" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="program" label="Program" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="cohort" label="Start → End" sort={sort} onSort={onSort} /></Th>
        <Th><SortHeader id="sales_person" label="Sales Person" sort={sort} onSort={onSort} /></Th>
        <Th align="right"><SortHeader id="total_revenue" label="Booking (TOV)" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="recognised_revenue" label="Revenue" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right"><SortHeader id="collected" label="Total Paid" sort={sort} onSort={onSort} align="right" /></Th>
        <Th align="right" className="pr-0"><SortHeader id="outstanding" label="Outstanding" sort={sort} onSort={onSort} align="right" /></Th>
      </tr>
    </thead>
    <tbody>
      {(data?.rows || []).map((r) => (
        <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
          <td className="py-2 pr-3 text-xs text-muted-foreground tabular whitespace-nowrap">
            {r.enrolment_date || "—"}
          </td>
          <td className="py-2 pr-3 font-semibold" title={r.name}>{r.name || "—"}</td>
          <td className="py-2 pr-3">
            <Tag label={r.client_type || "Partnership"} color={PAYER_TYPE_COLOR.Partnership} />
          </td>
          <td className="py-2 pr-3">
            <Tag label={r.status} color={STATUS_COLOR[r.status] || "#94a3b8"} />
          </td>
          <ListCell items={r.program} />
          <ListCell items={r.cohort} />
          <ListCell items={r.sales_person} />
          <MoneyCell value={r.total_revenue} bold />
          <MoneyCell value={r.recognised_revenue} color="#E76F1A" />
          <MoneyCell value={r.collected} color="#2BA88A" />
          <MoneyCell value={r.outstanding} color={r.outstanding > 0 ? "#B23A48" : "#94a3b8"} bold className="pr-0" />
        </tr>
      ))}
    </tbody>
  </table>
);

// -------------------- Main slide-over (Sheet) --------------------

const FETCHERS = {
  bookings:     { fn: "students",             defaultSort: { by: "total_revenue", dir: "desc" } },
  revenue:      { fn: "drilldownRevenue",     defaultSort: { by: "recognised_amount", dir: "desc" } },
  collection:   { fn: "payments",             defaultSort: { by: "payment_date", dir: "desc" } },
  "collection-orphan": { fn: "payments",      defaultSort: { by: "payment_date", dir: "desc" }, extraParams: { orphan: true } },
  outstanding:  { fn: "drilldownOutstanding", defaultSort: { by: "outstanding", dir: "desc" } },
  dropouts:     { fn: "drilldownRevenue",     defaultSort: { by: "refund_eligible", dir: "desc" }, extraParams: { dropped_only: true } },
  students:     { fn: "studentsOnly",         defaultSort: { by: "enrolment_date", dir: "desc" } },
  partnerships: { fn: "partnerships",         defaultSort: { by: "enrolment_date", dir: "desc" } },
  // salespeople: special non-paginated endpoint handled inline below
};

const TITLES = {
  bookings:     { gross: "Total Bookings (TOV)",     net: "Net Bookings" },
  revenue:      { gross: "Revenue (Recognized)",     net: "Net Revenue (Recognised)" },
  collection:   { gross: "Total Collection",         net: "Net Collection" },
  "collection-orphan": { gross: "Orphan Payments (no vertical)", net: "Orphan Payments (no vertical)" },
  outstanding:  { gross: "Outstanding (Pipeline)",   net: "Net Outstanding" },
  dropouts:     { gross: "Drop-out Exposure",        net: "Drop-out Exposure" },
  students:     { gross: "Total Students",           net: "Total Students" },
  partnerships: { gross: "Total Partnerships",       net: "Total Partnerships" },
  salespeople:  { gross: "Active Sales People",      net: "Active Sales People (Net)" },
};

const Spinner = () => (
  <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
);

const Empty = ({ msg }) => (
  <div className="py-10 text-center text-sm text-muted-foreground">{msg}</div>
);

export const KpiDrilldown = ({ kpi, isNet, params, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(
    kpi && FETCHERS[kpi] ? FETCHERS[kpi].defaultSort : { by: "revenue", dir: "desc" }
  );

  // Salespeople-specific in-memory state (data already non-paginated from server)
  const [allSP, setAllSP] = useState([]);

  // Reset when kpi changes
  useEffect(() => {
    if (kpi) {
      setSearch("");
      setSort(
        FETCHERS[kpi]
          ? FETCHERS[kpi].defaultSort
          : { by: "revenue", dir: "desc" }
      );
      setData(null);
      setAllSP([]);
    }
  }, [kpi]);

  const load = useCallback(async () => {
    if (!kpi) return;
    setLoading(true);
    try {
      if (kpi === "salespeople") {
        const res = await api.bySalesperson(params);
        setAllSP(res.rows || []);
      } else {
        const cfg = FETCHERS[kpi];
        const res = await api[cfg.fn]({
          ...params,
          ...(cfg.extraParams || {}),
          search: search || undefined,
          sort_by: sort.by,
          sort_dir: sort.dir,
          page: 1,
          page_size: PAGE_SIZE,
        });
        setData(res);
      }
    } catch (e) {
      logError(e);
    } finally {
      setLoading(false);
    }
  }, [kpi, params, search, sort]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side sort+filter for salespeople (single-page, no pagination)
  const spData = (() => {
    if (kpi !== "salespeople") return null;
    const keyMap = {
      name: (r) => (r.name || "").toLowerCase(),
      designation: (r) => (r.designation || "").toLowerCase(),
      revenue: (r) => Number(r.revenue || 0),
      recognised_revenue: (r) => Number(r.recognised_revenue || 0),
      outstanding: (r) => Number(r.outstanding || 0),
      collection: (r) => Number(r.collection || 0),
      students: (r) => Number(r.students || 0),
      partnerships: (r) => Number(r.partnerships || 0),
      avg_deal_size: (r) => Number(r.avg_deal_size || 0),
    };
    const fn = keyMap[sort.by] || keyMap.revenue;
    const dirMul = sort.dir === "asc" ? 1 : -1;
    const filtered = search.trim()
      ? allSP.filter(
          (r) =>
            (r.name || "").toLowerCase().includes(search.toLowerCase()) ||
            (r.designation || "").toLowerCase().includes(search.toLowerCase())
        )
      : allSP;
    const sorted = [...filtered].sort((a, b) => {
      const va = fn(a);
      const vb = fn(b);
      if (va < vb) return -1 * dirMul;
      if (va > vb) return 1 * dirMul;
      return 0;
    });
    return { rows: sorted, total: sorted.length };
  })();

  const isSP = kpi === "salespeople";
  const activeData = isSP ? spData : data;
  const total = activeData?.total || 0;

  const summaryAmount = (() => {
    if (!data) return null;
    if (kpi === "revenue") return data.total_recognised;
    if (kpi === "outstanding") return data.sum_outstanding;
    if (kpi === "collection") return data.total_amount;
    return null;
  })();

  return (
    <Sheet
      open={!!kpi}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-screen sm:max-w-none p-0 flex flex-col gap-0 border-l"
        data-testid="kpi-drilldown-sheet"
      >
        <SheetHeader className="px-5 sm:px-8 pt-5 pb-3 border-b border-border/60 bg-secondary/30 shrink-0 text-left space-y-0">
          <SheetTitle className="font-display text-xl sm:text-2xl tracking-tight pr-8">
            {kpi ? TITLES[kpi][isNet ? "net" : "gross"] : ""}
          </SheetTitle>
        </SheetHeader>

        <div className="px-5 sm:px-8 pt-3 pb-2 flex flex-wrap items-center gap-3 shrink-0">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                isSP
                  ? "Search sales person…"
                  : "Search payer / payer type / program / cohort / sales person…"
              }
              className="h-9 pl-9 text-sm"
              data-testid="drill-search"
            />
          </div>
          <div className="text-xs text-muted-foreground tabular ml-auto flex items-center gap-3 flex-wrap justify-end">
            {summaryAmount != null && (
              <span>
                <span className="text-muted-foreground">Sum in range:</span>{" "}
                <span className="font-semibold text-foreground">
                  {formatINR(summaryAmount)}
                </span>
                {kpi === "outstanding" && data?.sum_collected != null && (
                  <span className="ml-2 text-muted-foreground">
                    · Collected {formatINR(data.sum_collected)}
                  </span>
                )}
              </span>
            )}
            {total > 0 && (
              <span data-testid="drill-record-count">
                {formatNum(total)} record{total === 1 ? "" : "s"}
              </span>
            )}
            {activeData && (activeData.rows || []).length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-3 gap-1.5 text-xs"
                onClick={() =>
                  downloadCSV(
                    kpi,
                    activeData.rows,
                    TITLES[kpi]?.[isNet ? "net" : "gross"]
                  )
                }
                data-testid="drill-csv"
              >
                <Download className="w-3.5 h-3.5" />
                Download CSV
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto px-5 sm:px-8 pb-6">
          {loading && !activeData && <Spinner />}
          {activeData && (activeData.rows || []).length === 0 && !loading && (
            <Empty msg="No records match the current filters." />
          )}
          {activeData && (activeData.rows || []).length > 0 && (
            <>
              {kpi === "bookings"     && <BookingsBody     data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "revenue"      && <RevenueBody      data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "collection"   && <CollectionBody   data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "collection-orphan" && <CollectionBody data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "outstanding"  && <OutstandingBody  data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "dropouts"     && <DropoutsBody     data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "students"     && <StudentsBody     data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "partnerships" && <PartnershipsBody data={activeData} sort={sort} onSort={setSort} />}
              {kpi === "salespeople"  && <SalespeopleBody  data={activeData} sort={sort} onSort={setSort} />}
            </>
          )}
        </div>

        {activeData && (activeData.rows || []).length > 0 && (
          <div className="shrink-0 border-t border-border/60 bg-secondary/40 backdrop-blur px-5 sm:px-8 py-2">
            <TotalsRow kpi={kpi} rows={activeData.rows} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
